import React, { useState, useEffect, useMemo } from 'react';
import {
  Row,
  Col,
  Button,
  Container,
  FormSelect,
  FormInput,
  FormCheckbox,
  Alert
} from 'shards-react';
import { Paragraph } from '@increase/typed-components';
import { differenceInDays } from 'date-fns';
import PageTitle from '../../../components/PageTitle';
import RegisteredInfoProvidersSelect from '../../../components/inputs/RegisteredInfoProvidersSelect';
import BulkReparseService from '../../../services/BulkReparseService';
import Constants from '../../../data/Constants';
import { Can } from '../../../permissions/helper';
import { useUserEmail } from '../../../contexts/AuthContext';
import { useRegisteredParsers } from '../../../hooks/queries/useRegisteredParsers';
import { useParserVersions } from '../../../hooks/queries/useParserVersions';
import { useBulkReparseCandidatesCount } from '../../../hooks/queries/useBulkReparseCandidatesCount';
import CantAuthorize from '../../../components/common/CantAuthorize';
import BulkReparseTracker from './BulkReparseTracker';

const NEW_VERSION_WINDOW_DAYS = 30;
const MIN_START_DATE_LOOKBACK_MONTHS = 6;

const isRecentlyChanged = (validFrom) => {
  if (!validFrom) return false;
  return differenceInDays(Date.now(), new Date(validFrom)) <= NEW_VERSION_WINDOW_DAYS;
};

// Local getters (not toISOString) on purpose: converting to UTC can shift the date by a day
// depending on the browser's timezone, which would make the floor off-by-one.
const formatDateInput = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

// Computed once per page load, not hardcoded, so the floor keeps sliding forward with "today"
// instead of going stale like a fixed cutoff date would.
const getMinStartDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - MIN_START_DATE_LOOKBACK_MONTHS);
  return date;
};
const MIN_START_DATE_OBJ = getMinStartDate();
const MIN_START_DATE = formatDateInput(MIN_START_DATE_OBJ);
const MIN_START_DATE_LABEL = MIN_START_DATE_OBJ.toLocaleDateString();

const documentTypeLabel = (value) =>
  Constants.documentTypes.find((option) => option.value === value)?.label || value;

// Sorted by label, not registration order, so the dropdown reads consistently regardless of
// how the parser configuration files happen to be laid out on disk.
const distinctOptions = (values, labelFor) =>
  [...new Set(values)]
    .map((value) => ({ value, label: labelFor(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));

// Parsers::Registry still has both format_types registered for these two combinations, but per
// the architect that's a historical artifact, not a live choice: "grrcn"/"px" were patches
// introduced when the provider changed its format, and the original "default" parser is only
// still there for old documents — nothing being scraped today produces it anymore. Asking the
// user to pick between them would be offering a dead option, so these are forced instead of
// left to the format_type dropdown.
const FORCED_FORMAT_TYPES = {
  amex: { electronic_payments: 'grrcn' },
  visa: { electronic_payments: 'px' }
};

export function SupportBulkReparse() {
  const userEmail = useUserEmail();

  // Only combinations Parsers::Registry actually has a parser for, for this tenant — instead
  // of the frontend's static (and country-agnostic) Constants lists, which include providers/
  // document types that don't apply to Argentina or aren't registered at all. The provider
  // select below shares this same cached query, so it only hits the network once per session.
  const { registeredParsers } = useRegisteredParsers();
  const [infoProvider, setInfoProvider] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [formatType, setFormatType] = useState('');
  const [versionId, setVersionId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errorOnly, setErrorOnly] = useState(false);
  const [unbalancedOnly, setUnbalancedOnly] = useState(false);
  const [allowOldDates, setAllowOldDates] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [trackerJobId, setTrackerJobId] = useState(null);
  // Snapshot of buildFilter() at the time of the last successful search — compared against the
  // current filter on every render so that changing anything after searching (without
  // searching again) invalidates the count instead of silently reparsing under a stale number.
  const [searchedFilterSignature, setSearchedFilterSignature] = useState(null);

  const { versions } = useParserVersions(documentType, infoProvider, formatType);
  const candidateSearch = useBulkReparseCandidatesCount();

  const documentTypeOptions = useMemo(
    () =>
      distinctOptions(
        registeredParsers.filter((p) => p.infoProvider === infoProvider).map((p) => p.documentType),
        documentTypeLabel
      ),
    [registeredParsers, infoProvider]
  );

  // Most info_provider/document_type combinations only have one format_type, in which case
  // there's nothing to ask the user — auto-selected below and the picker stays hidden. Only
  // shown when there's genuinely more than one live option (FORCED_FORMAT_TYPES overrides the
  // registry's list for the combinations where that isn't actually true).
  const formatTypeOptions = useMemo(() => {
    const forced = FORCED_FORMAT_TYPES[infoProvider]?.[documentType];
    if (forced) return [forced];

    return [
      ...new Set(
        registeredParsers
          .filter((p) => p.infoProvider === infoProvider && p.documentType === documentType)
          .map((p) => p.formatType)
      )
    ].sort();
  }, [registeredParsers, infoProvider, documentType]);

  useEffect(() => {
    setFormatType(formatTypeOptions.length === 1 ? formatTypeOptions[0] : '');
  }, [formatTypeOptions]);

  useEffect(() => {
    setVersionId('');
  }, [infoProvider, documentType, formatType]);

  const currentVersion = versions.find((v) => !v.validTo);
  const showNewVersionAlert = currentVersion && isRecentlyChanged(currentVersion.validFrom);

  const hasValidStartDate = Boolean(startDate) && (allowOldDates || startDate >= MIN_START_DATE);

  const buildFilter = () => ({
    infoProvider,
    documentType,
    formatType,
    versionId: versionId || undefined,
    startDate: startDate ? new Date(startDate).toISOString() : undefined,
    endDate: endDate ? new Date(endDate).toISOString() : undefined,
    errorOnly,
    unbalancedOnly,
    allowOldDates
  });

  const currentFilterSignature = useMemo(
    () => JSON.stringify(buildFilter()),
    [
      infoProvider,
      documentType,
      formatType,
      versionId,
      startDate,
      endDate,
      errorOnly,
      unbalancedOnly,
      allowOldDates
    ]
  );

  const hasFilledFilter =
    Boolean(infoProvider) && Boolean(documentType) && Boolean(formatType) && hasValidStartDate;
  const canSearch = hasFilledFilter && !candidateSearch.loading && !submitting;

  // A count from before the filters last changed can't be trusted anymore — the button stays
  // disabled and the message below tells the user to search again, rather than letting them
  // reparse under a number that no longer matches what's set.
  const isCountStale =
    candidateSearch.count !== null && searchedFilterSignature !== currentFilterSignature;
  const canSubmit =
    hasFilledFilter &&
    candidateSearch.count !== null &&
    !isCountStale &&
    candidateSearch.count > 0 &&
    !submitting;

  const onInfoProviderChange = (value) => {
    setInfoProvider(value);
    setDocumentType('');
  };

  const onSearch = async () => {
    const signature = currentFilterSignature;
    candidateSearch.search(buildFilter());
    setSearchedFilterSignature(signature);
  };

  const onSubmit = async () => {
    setSubmitError(null);
    setJobId(null);
    setTrackerJobId(null);
    setSubmitting(true);

    try {
      const job = await BulkReparseService().bulkReparse(buildFilter(), userEmail);
      setJobId(job.id);
      setTrackerJobId(job.id);
      setSearchedFilterSignature(null);
    } catch {
      setSubmitError(
        'No se pudo iniciar el reparseo masivo. Revisá los filtros e intentá de nuevo.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle className="text-sm-left" sm="4" subtitle="" title="Reparseo Masivo" />
      </Row>

      <Can action="sup:reparse" renderIfCant={<CantAuthorize />}>
        <Row>
          <Col lg="4" sm="12" xs="12">
            <RegisteredInfoProvidersSelect
              isClearable
              onChange={(value) => onInfoProviderChange(value || '')}
              placeholder="Info provider..."
              value={infoProvider}
            />
          </Col>
          <Col lg="4" sm="12" xs="12">
            <FormSelect
              disabled={!infoProvider}
              id="documentType"
              onChange={(e) => setDocumentType(e.target.value)}
              value={documentType}
            >
              <option value="">Tipo de documento...</option>
              {documentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormSelect>
          </Col>
          {formatTypeOptions.length > 1 && (
            <Col lg="4" sm="12" xs="12" className="mt-2 mt-lg-0">
              <FormSelect
                id="formatType"
                onChange={(e) => setFormatType(e.target.value)}
                value={formatType}
              >
                <option value="">Formato...</option>
                {formatTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </FormSelect>
            </Col>
          )}
        </Row>

        <Row className="mt-3">
          <Col lg="4" sm="6" xs="12">
            <FormInput
              id="startDate"
              min={allowOldDates ? undefined : MIN_START_DATE}
              onChange={(e) => setStartDate(e.target.value)}
              type="date"
              value={startDate}
            />
          </Col>
          <Col lg="4" sm="6" xs="12">
            <FormInput
              id="endDate"
              onChange={(e) => setEndDate(e.target.value)}
              type="date"
              value={endDate}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg="8" sm="12" xs="12">
            <FormCheckbox
              checked={allowOldDates}
              onChange={(e) => setAllowOldDates(e.target.checked)}
            >
              Permitir fecha desde anterior a {MIN_START_DATE_LABEL} (
              {MIN_START_DATE_LOOKBACK_MONTHS} meses atrás)
            </FormCheckbox>
          </Col>
        </Row>

        {allowOldDates && (
          <Row className="mt-2">
            <Col>
              <Alert theme="warning">
                Estás reparseando más allá de los últimos {MIN_START_DATE_LOOKBACK_MONTHS} meses.
                Puede tardar bastante más y generar más carga en el servidor.
              </Alert>
            </Col>
          </Row>
        )}

        {showNewVersionAlert && (
          <Alert theme="warning">
            Versión {currentVersion.version} vigente desde{' '}
            {new Date(currentVersion.validFrom).toLocaleDateString()}. Puede haber documentos
            parseados con una versión anterior.
          </Alert>
        )}

        <Row className="mt-3">
          <Col lg="4" sm="12" xs="12">
            <FormSelect
              disabled={versions.length === 0}
              id="versionId"
              onChange={(e) => setVersionId(e.target.value)}
              value={versionId}
            >
              <option value="">Versión exacta (opcional, además del rango de fechas)...</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.version} {version.validTo ? '(histórica)' : '(vigente)'}
                </option>
              ))}
            </FormSelect>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg="4" sm="6" xs="12">
            <FormCheckbox checked={errorOnly} onChange={(e) => setErrorOnly(e.target.checked)}>
              Solo documentos con error
            </FormCheckbox>
          </Col>
          <Col lg="4" sm="6" xs="12">
            <FormCheckbox
              checked={unbalancedOnly}
              onChange={(e) => setUnbalancedOnly(e.target.checked)}
            >
              Solo documentos desbalanceados
            </FormCheckbox>
          </Col>
        </Row>

        {!hasValidStartDate && (
          <Row className="mt-2">
            <Col>
              <Paragraph>
                La fecha desde es obligatoria. Por defecto no puede ser anterior al{' '}
                {MIN_START_DATE_LABEL} — para reparsear un rango más viejo, tildá &quot;permitir
                fecha desde anterior...&quot; arriba. La versión exacta es un filtro adicional
                opcional.
              </Paragraph>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col className="d-flex align-items-center">
            <Button disabled={!canSearch} onClick={onSearch} theme="secondary">
              {candidateSearch.loading ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button className="ml-2" disabled={!canSubmit} onClick={onSubmit}>
              {submitting ? 'Iniciando...' : 'Reparsear'}
            </Button>
          </Col>
        </Row>

        {candidateSearch.error && (
          <Row className="mt-2">
            <Col>
              <Alert theme="danger">{candidateSearch.error}</Alert>
            </Col>
          </Row>
        )}

        {candidateSearch.count !== null && !isCountStale && (
          <Row className="mt-2">
            <Col>
              <Paragraph>
                {candidateSearch.count === 0
                  ? 'No hay documentos que coincidan con estos filtros.'
                  : `Se van a reparsear ${candidateSearch.count} documento${candidateSearch.count === 1 ? '' : 's'}.`}
              </Paragraph>
            </Col>
          </Row>
        )}

        {candidateSearch.count !== null && isCountStale && (
          <Row className="mt-2">
            <Col>
              <Paragraph>
                Los filtros cambiaron desde la última búsqueda — volvé a buscar para habilitar el
                reparseo.
              </Paragraph>
            </Col>
          </Row>
        )}

        {submitError && (
          <Row className="mt-3">
            <Col>
              <Alert theme="danger">{submitError}</Alert>
            </Col>
          </Row>
        )}

        {jobId && (
          <Row className="mt-3">
            <Col>
              <Paragraph>
                Tarea iniciada. Seguí el estado con el ID <code>{jobId}</code>
              </Paragraph>
            </Col>
          </Row>
        )}

        <BulkReparseTracker jobId={trackerJobId} onJobSearch={setTrackerJobId} />
      </Can>
    </Container>
  );
}
